import React, {
  useEffect, useRef, useState,
} from 'react';
import { useIntl } from '@edx/frontend-platform/i18n';
import { getAuthenticatedUser } from '@edx/frontend-platform/auth';
import { SlRequestCloseEvent } from '@shoelace-style/shoelace/cdn/events/sl-request-close';

import SlButton from '@shoelace-style/shoelace/dist/react/button';
import SlDialog from '@shoelace-style/shoelace/dist/react/dialog';

import { cohortsApi } from '../../api';
import type { CohortMember, CohortRole } from '../../types';

import messages from './messages';

type ActionType = 'promote' | 'demote' | 'remove';

/** Shoelace alert variant used for a member-action toast. */
export type ToastVariant = 'success' | 'danger' | 'primary';

interface MenuItem {
  type: ActionType;
  label: string;
  danger: boolean;
}

interface MemberActionsMenuProps {
  slug: string;
  /** The row this menu manages (only role/identity fields are read). */
  member: CohortMember;
  /** The caller's own role in this cohort (`detail.my_role`). */
  callerRole: CohortRole;
  onMutated: (wasRemoval: boolean) => void;
  /**
   * Raise a toast on the stable parent view. The alert host lives in
   * `CohortMembers`, not this per-row component, so the feedback survives the
   * row unmounting when a refetch drops the just-removed member.
   */
  onToast: (variant: ToastVariant, msg: string) => void;
}

/** Is `member` the caller's own row? Prefer numeric user id, fall back to username. */
const isOwnRow = (member: CohortMember): boolean => {
  const user = getAuthenticatedUser();
  if (!user) {
    return false;
  }
  if (typeof user.userId === 'number') {
    return user.userId === member.user_id;
  }
  return user.username === member.username;
};

/**
 * Kebab (⋮) actions menu for one member row. Owner may promote/demote/remove;
 * co-facilitator may only remove plain members; never on the owner row or the
 * caller's own row — the server remains the authority. Each action opens a
 * confirm dialog; confirming fires the API and refreshes the page.
 */
const MemberActionsMenu: React.FC<MemberActionsMenuProps> = ({
  slug, member, callerRole, onMutated, onToast,
}) => {
  const intl = useIntl();

  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState<ActionType | null>(null);
  const [busy, setBusy] = useState(false);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLUListElement | null>(null);
  // Keeps the dialog copy stable while it animates closed (after `pending`
  // resets to null), so a promote/demote dialog never flashes the remove copy.
  const lastActionRef = useRef<ActionType>('remove');

  const displayName = member.name || member.username;

  // Build the action set from (caller role × target role), excluding the owner
  // row and the caller's own row. An empty set means this row gets no menu.
  const items: MenuItem[] = [];
  if (member.role !== 'owner' && !isOwnRow(member)) {
    if (callerRole === 'owner') {
      if (member.role === 'member') {
        items.push({
          type: 'promote',
          label: intl.formatMessage(messages.memberActionsMakeCoFacilitator),
          danger: false,
        });
      } else if (member.role === 'co_facilitator') {
        items.push({
          type: 'demote',
          label: intl.formatMessage(messages.memberActionsRevokeCoFacilitator),
          danger: false,
        });
      }
      items.push({
        type: 'remove',
        label: intl.formatMessage(messages.memberActionsRemoveMember),
        danger: true,
      });
    } else if (callerRole === 'co_facilitator' && member.role === 'member') {
      items.push({
        type: 'remove',
        label: intl.formatMessage(messages.memberActionsRemoveMember),
        danger: true,
      });
    }
  }

  // Move focus into the menu when it opens so it is keyboard-reachable.
  useEffect(() => {
    if (open) {
      const first = menuRef.current?.querySelector<HTMLButtonElement>('[role="menuitem"]');
      first?.focus();
    }
  }, [open]);

  // Close on any click outside the menu; the trigger's own handler toggles it.
  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const onDocMouseDown = (event: MouseEvent): void => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [open]);

  if (items.length === 0) {
    return null;
  }

  const closeMenu = (returnFocus: boolean): void => {
    setOpen(false);
    if (returnFocus) {
      triggerRef.current?.focus();
    }
  };

  const handleMenuKeyDown = (event: React.KeyboardEvent<HTMLUListElement>): void => {
    if (event.key === 'Escape') {
      event.preventDefault();
      closeMenu(true);
      return;
    }
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') {
      return;
    }
    event.preventDefault();
    const nodes = Array.from(
      menuRef.current?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]') ?? [],
    );
    if (nodes.length === 0) {
      return;
    }
    const current = nodes.indexOf(document.activeElement as HTMLButtonElement);
    const delta = event.key === 'ArrowDown' ? 1 : -1;
    const nextIndex = (current + delta + nodes.length) % nodes.length;
    nodes[nextIndex]?.focus();
  };

  // Prevent an accidental overlay click from silently dropping a pending action.
  const handleRequestClose = (event: SlRequestCloseEvent): void => {
    if (event.detail.source === 'overlay') {
      event.preventDefault();
    }
  };

  const runPending = (): void => {
    if (!pending) {
      return;
    }
    const action = pending;
    setBusy(true);

    const onSuccess = (successMessage: { id: string; defaultMessage: string; description: string }): void => {
      setBusy(false);
      setPending(null);
      onToast('success', intl.formatMessage(successMessage, { name: displayName }));
      onMutated(action === 'remove');
    };

    const onError = (error: unknown): void => {
      setBusy(false);
      setPending(null);
      const status = (error as { status?: number })?.status;
      if (status === 403) {
        onToast('danger', intl.formatMessage(messages.memberActionsToastNotAllowed));
      } else if (status === 404) {
        // The member is already gone: surface it as info and refresh the list.
        onToast('primary', intl.formatMessage(messages.memberActionsToastAlreadyGone));
        onMutated(true);
      } else {
        onToast('danger', intl.formatMessage(messages.memberActionsToastGenericError));
      }
    };

    if (action === 'promote') {
      cohortsApi.updateCohortMemberRole(
        slug,
        member.user_id,
        'co_facilitator',
        () => onSuccess(messages.memberActionsToastPromoted),
        onError,
      );
    } else if (action === 'demote') {
      cohortsApi.updateCohortMemberRole(
        slug,
        member.user_id,
        'member',
        () => onSuccess(messages.memberActionsToastDemoted),
        onError,
      );
    } else {
      cohortsApi.removeCohortMember(
        slug,
        member.user_id,
        () => onSuccess(messages.memberActionsToastRemoved),
        onError,
      );
    }
  };

  const dialogCopy = (): { title: string; body: string; confirm: string; danger: boolean } => {
    switch (pending ?? lastActionRef.current) {
      case 'promote':
        return {
          title: intl.formatMessage(messages.memberActionsConfirmPromoteTitle),
          body: intl.formatMessage(messages.memberActionsConfirmPromoteBody, { name: displayName }),
          confirm: intl.formatMessage(messages.memberActionsConfirmPromoteConfirm),
          danger: false,
        };
      case 'demote':
        return {
          title: intl.formatMessage(messages.memberActionsConfirmDemoteTitle),
          body: intl.formatMessage(messages.memberActionsConfirmDemoteBody, { name: displayName }),
          confirm: intl.formatMessage(messages.memberActionsConfirmDemoteConfirm),
          danger: false,
        };
      default:
        return {
          title: intl.formatMessage(messages.memberActionsConfirmRemoveTitle),
          body: intl.formatMessage(messages.memberActionsConfirmRemoveBody, { name: displayName }),
          confirm: intl.formatMessage(messages.memberActionsConfirmRemoveConfirm),
          danger: true,
        };
    }
  };

  const copy = dialogCopy();

  return (
    <div className="cohort-member-actions" ref={containerRef}>
      <button
        type="button"
        ref={triggerRef}
        className="cohort-member-actions-trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={intl.formatMessage(messages.memberActionsMenuLabel)}
        onClick={() => setOpen((value) => !value)}
      >
        <span aria-hidden="true">⋮</span>
      </button>

      {open && (
        <ul
          ref={menuRef}
          className="cohort-member-actions-menu"
          role="menu"
          aria-label={intl.formatMessage(messages.memberActionsMenuLabel)}
          onKeyDown={handleMenuKeyDown}
        >
          {items.map((item) => (
            <li key={item.type} role="none">
              <button
                type="button"
                role="menuitem"
                className={`cohort-member-actions-item${item.danger ? ' cohort-member-actions-item-danger' : ''}`}
                onClick={() => {
                  // Focus the trigger while it is still mounted, or SlDialog
                  // captures <body> and focuses that on close.
                  closeMenu(true);
                  lastActionRef.current = item.type;
                  setPending(item.type);
                }}
              >
                {item.label}
              </button>
            </li>
          ))}
        </ul>
      )}

      <SlDialog
        className="cohort-member-actions-dialog"
        label={copy.title}
        open={pending !== null}
        onSlRequestClose={handleRequestClose}
        onSlAfterHide={() => setPending(null)}
      >
        <p className="leg-p">{copy.body}</p>
        <SlButton
          className="cohort-member-actions-cancel-btn"
          slot="footer"
          disabled={busy}
          onClick={() => setPending(null)}
        >
          {intl.formatMessage(messages.memberActionsConfirmCancel)}
        </SlButton>
        <SlButton
          className={`cohort-member-actions-confirm-btn${copy.danger ? ' cohort-member-actions-confirm-btn-danger' : ''}`}
          slot="footer"
          variant={copy.danger ? 'danger' : 'primary'}
          loading={busy}
          // `disabled` is what blocks a second click; `loading` is styling only.
          disabled={busy}
          onClick={runPending}
        >
          {copy.confirm}
        </SlButton>
      </SlDialog>
    </div>
  );
};

export default MemberActionsMenu;
